import React from "react";
import ReactDOMServer from "react-dom/server";

import { NodeType } from "prosemirror-model";
import { EditorState, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import uuid from "uuid";

import { launchCreateTagModal, TagComponent, type TagType } from "~/tags";

import { getContext } from "./context";
import { TypeAheadProvider, type TypeAheadResult } from "./typeahead";

const CreateTagItem = ({ partialLabel }: { partialLabel: string }) => {
	return (
		<div>
			<small>Create new workspace tag</small>
			<br />
			<TagComponent color="NO_COLOR" label={partialLabel} />
		</div>
	);
};

type TagTypeWithId = TagType & { id: string };

export default class TagProvider extends TypeAheadProvider {
	items: TypeAheadResult[] = [];
	allowCreatingNewTags: boolean;

	constructor(options: { viewerIsGuest: boolean; createEnabled?: boolean }) {
		super(options);
		this.allowCreatingNewTags = !options.viewerIsGuest && (options.createEnabled ?? true);
	}

	get key() {
		return "#";
	}

	get replaceMatch() {
		return false;
	}

	search(_view: EditorView, searchString: string): TypeAheadResult[] {
		const context = getContext(_view.state);
		const updatedTags: Array<TagTypeWithId> | undefined = context?.tags;

		if (updatedTags) {
			this.items = updatedTags.map(
				({ id, label, color }): TypeAheadResult => ({
					id,
					title: label,
					command(editorState, typeAheadState) {
						const selection = typeAheadState?.matchingSelection?.resolve(editorState.doc);
						if (!selection) return null;

						return (state: EditorState, dispatch?: (tr: Transaction) => void) => {
							const nodeType: NodeType = editorState.schema.nodes["tag"];
							const { tr } = state;

							selection.replaceWith(tr, nodeType.create({ entityId: uuid.v4() }, state.schema.text(`#${label}`)));

							if (dispatch) {
								dispatch(tr);
							}

							return true;
						};
					},
					render: container => {
						container.innerHTML = ReactDOMServer.renderToStaticMarkup(<TagComponent color={color} label={label} />);
						return true;
					},
				}),
			);
		}

		if (searchString === "")
			return this.items.sort(({ id: a }, { id: b }) => a.toLocaleString().localeCompare(b.toLocaleString()));
		// Any changes to this regex also need to be applied to apps/notes/models/tags.py
		else if (searchString.length > 32 || !/^[\w-]*[a-zA-Z]+[\w-]*$/.test(searchString)) return [];

		const searchResults = this.items
			.map(item => ({
				...item,
				score: this.fuzzy(item.title, searchString),
			}))
			.filter(({ score }) => typeof score === "number")
			.sort(({ score: a }, { score: b }) => (b as number) - (a as number));

		if (this.items.map(({ title }) => title).includes(searchString.toLocaleLowerCase())) return searchResults;

		if (!this.allowCreatingNewTags) return searchResults;

		const createTagOption: TypeAheadResult = {
			id: `searchString: ${searchString}`,
			title: searchString,
			command: (editorState, typeAheadState) => {
				const selection = typeAheadState?.matchingSelection?.resolve(editorState.doc);
				if (!selection) return null;

				return (state: EditorState, dispatch?: (tr: Transaction) => void, view?: EditorView) => {
					const { tr } = state;

					// Remove the "written" tag
					selection.replace(tr);

					if (dispatch) {
						dispatch(tr);
					}

					launchCreateTagModal({ view, startingLabel: searchString });
					return true;
				};
			},
			render: container => {
				container.innerHTML = ReactDOMServer.renderToStaticMarkup(<CreateTagItem partialLabel={searchString} />);
				return true;
			},
		};

		return [createTagOption, ...searchResults];
	}
}
